(() => {
  const MCGILL_PRODUCT_IDS = new Set(['11081302573249','11081304670401','11085742702785','11085780713665']);
  const ORDER_GROUP_PROPERTY = '_mmp_order_group';
  const MCGILL_ORDER_GROUP = 'mcgill-convocation';
  const BLANK_MESSAGE_VALUE = 'Leave blank';
  const fetchCart = window.fetch.bind(window);
  let cart = null;
  let saving = false;
  let dirty = false;
  let saveError = false;
  let saveTimer;
  let refreshTimer;
  let cleanupTimer;
  let desiredVersion = 0;
  let draft = { on: false, text: '', leaveBlank: false };

  const normalizeGroup = (value) => String(value ?? '').trim().toLowerCase();

  function itemIsMcGill(item) {
    return normalizeGroup(item?.properties?.[ORDER_GROUP_PROPERTY]) === MCGILL_ORDER_GROUP ||
      MCGILL_PRODUCT_IDS.has(String(item?.product_id ?? '').trim());
  }

  function attrs(c) {
    return c?.attributes || {};
  }

  function regularOnly(c) {
    return c?.item_count > 0 && (c.items || []).every((item) => !itemIsMcGill(item));
  }

  function hasMcGill(c) {
    return c?.item_count > 0 && (c.items || []).some((item) => itemIsMcGill(item));
  }

  function selected(c) {
    return String(attrs(c)['Complimentary Message Card'] || '').toLowerCase() === 'yes';
  }

  function rawMessage(c) {
    return String(attrs(c)['Card Message'] || '');
  }

  function stateFromCart(c) {
    const raw = rawMessage(c);
    return {
      on: selected(c),
      text: raw === BLANK_MESSAGE_VALUE ? '' : raw,
      leaveBlank: raw === BLANK_MESSAGE_VALUE
    };
  }

  function draftComplete() {
    return !draft.on || draft.leaveBlank || draft.text.trim().length > 0;
  }

  function cartContext(el) {
    return location.pathname.replace(/\/$/, '').endsWith('/cart') ||
      !!el?.closest('.mini-cart,cart-drawer,[id="MiniCart"],[id*="mini-cart" i],form#cart');
  }

  function styles() {
    if (document.getElementById('mmp-card-style')) return;
    const style = document.createElement('style');
    style.id = 'mmp-card-style';
    style.textContent = `
      gift-wrapping, cart-note { display: none !important; }
      .mmp-card { margin: 0 0 18px; padding: 16px; border: 1px solid #d7d7d7; border-radius: 8px; background: #fff; text-align: left; }
      .mmp-card__toggle, .mmp-card__blank { display: flex; align-items: flex-start; gap: 10px; cursor: pointer; font-size: 15px; line-height: 1.4; }
      .mmp-card__toggle input, .mmp-card__blank input { width: 18px; height: 18px; margin: 1px 0 0; flex: 0 0 auto; }
      .mmp-card__body { margin-top: 12px; }
      .mmp-card__body[hidden] { display: none !important; }
      .mmp-card__body > label:first-child { display: grid; gap: 6px; font-size: 14px; font-weight: 600; }
      .mmp-card textarea { box-sizing: border-box; width: 100%; min-height: 96px; padding: 10px; resize: vertical; border: 1px solid #bbb; border-radius: 5px; background: #fff; font: inherit; }
      .mmp-card textarea:disabled { background: #f4f4f4; opacity: .7; }
      .mmp-card__blank { margin-top: 10px; font-size: 14px; }
      .mmp-card__hint { margin: 8px 0 0; font-size: 12px; opacity: .68; }
      .mmp-card__status { min-height: 16px; margin-top: 6px; font-size: 12px; opacity: .78; }
      .mmp-card__status.is-error { color: #b42318; opacity: 1; }
      .mmp-card.saving { opacity: .72; }
    `;
    document.head.appendChild(style);
  }

  function cleanLegacyUi() {
    document.querySelectorAll('gift-wrapping, cart-note').forEach((el) => el.remove());

    document.querySelectorAll('gift-note').forEach((el) => {
      const action = el.closest('.mini-cart__action');
      if (action) action.remove();
      else el.remove();
    });

    document.querySelectorAll('.mini-cart__actions').forEach((actions) => {
      actions.querySelectorAll('.mini-cart__action').forEach((action) => {
        if (action.querySelector('gift-note,cart-note')) action.remove();
      });
      if (!actions.children.length) actions.remove();
    });
  }

  function targets() {
    const out = [];
    document.querySelectorAll('.cart__ctas').forEach((checkout) => {
      if (cartContext(checkout) && checkout.parentElement) out.push(['page', checkout.parentElement, checkout]);
    });
    document.querySelectorAll('.mini-cart .button-container,cart-drawer .button-container').forEach((checkout) => {
      if (checkout.parentElement) out.push(['drawer', checkout.parentElement, checkout]);
    });
    return out;
  }

  function createCard(key) {
    const el = document.createElement('div');
    el.className = 'mmp-card';
    el.dataset.mmpCard = key;
    el.innerHTML = `
      <label class="mmp-card__toggle">
        <input type="checkbox" data-mmp-card-toggle>
        <span><strong>Add a complimentary message card</strong></span>
      </label>
      <div class="mmp-card__body" data-mmp-card-body hidden>
        <label>Card message
          <textarea rows="4" maxlength="300" data-mmp-card-message placeholder="Write your message here"></textarea>
        </label>
        <label class="mmp-card__blank">
          <input type="checkbox" data-mmp-card-blank>
          <span>Leave card blank</span>
        </label>
        <p class="mmp-card__hint">Enter a message or choose “Leave card blank.” One complimentary card per order.</p>
      </div>
      <div class="mmp-card__status" data-mmp-card-status aria-live="polite"></div>
    `;
    return el;
  }

  function statusForDraft() {
    if (saveError) return ['Could not save the card message. Please try again.', true];
    if (saving || dirty) return ['Saving…', false];
    if (draft.on && !draftComplete()) return ['Please enter a card message or choose “Leave card blank.”', true];
    return ['', false];
  }

  function syncCard(el) {
    const toggle = el.querySelector('[data-mmp-card-toggle]');
    const body = el.querySelector('[data-mmp-card-body]');
    const textarea = el.querySelector('[data-mmp-card-message]');
    const blank = el.querySelector('[data-mmp-card-blank]');
    const status = el.querySelector('[data-mmp-card-status]');
    const [statusText, isError] = statusForDraft();

    toggle.checked = draft.on;
    body.hidden = !draft.on;
    textarea.value = draft.on ? draft.text : '';
    textarea.disabled = draft.leaveBlank;
    blank.checked = draft.on && draft.leaveBlank;
    el.classList.toggle('saving', saving || dirty);
    status.textContent = statusText;
    status.classList.toggle('is-error', isError);
  }

  function syncAllCards() {
    document.querySelectorAll('[data-mmp-card]').forEach(syncCard);
    updateCheckoutState();
  }

  function checkoutBlocked() {
    if (!cart || !regularOnly(cart)) return false;
    return saving || dirty || saveError || (draft.on && !draftComplete());
  }

  function updateCheckoutState() {
    const block = checkoutBlocked();
    document.querySelectorAll('button[name="checkout"],input[name="checkout"]').forEach((button) => {
      if (!cartContext(button)) return;
      if (block) {
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        button.dataset.mmpCardDisabled = '1';
      } else if (button.dataset.mmpCardDisabled) {
        if (button.dataset.mmpDdDisabled !== '1' && button.dataset.mcgillGuardDisabled !== 'true') {
          button.disabled = cart?.item_count === 0;
          if (!button.disabled) button.removeAttribute('aria-disabled');
        }
        delete button.dataset.mmpCardDisabled;
      }
    });
  }

  async function readCart() {
    const response = await fetchCart('/cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('cart read failed');
    return response.json();
  }

  async function updateAttributes(changes) {
    const response = await fetchCart('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: { ...attrs(cart), ...changes } })
    });
    if (!response.ok) throw new Error('cart update failed');
    return response.json();
  }

  function queueSave(delay = 350) {
    if (!cart || !regularOnly(cart)) return;
    desiredVersion += 1;
    dirty = true;
    saveError = false;
    clearTimeout(saveTimer);
    syncAllCards();
    saveTimer = setTimeout(flushSave, delay);
  }

  async function flushSave() {
    clearTimeout(saveTimer);
    if (saving || !cart || !regularOnly(cart) || !dirty) return;

    const version = desiredVersion;
    const state = { ...draft };
    saving = true;
    saveError = false;
    syncAllCards();

    try {
      cart = await updateAttributes({
        'Complimentary Message Card': state.on ? 'Yes' : '',
        'Card Message': state.on ? (state.leaveBlank ? BLANK_MESSAGE_VALUE : state.text.trim()) : ''
      });
      saveError = false;
      if (version === desiredVersion) {
        dirty = false;
        draft = stateFromCart(cart);
      }
    } catch (error) {
      saveError = true;
      dirty = true;
    } finally {
      saving = false;
      render(cart, { preserveDraft: dirty || saveError });
      if (!saveError && dirty && version !== desiredVersion) {
        saveTimer = setTimeout(flushSave, 0);
      }
    }
  }

  async function clearIncompatibleAttributes(c) {
    if (saving) return;
    const a = attrs(c);
    const hasNewCard = !!a['Complimentary Message Card'] || !!a['Card Message'];
    const hasLegacyGift = !!a['gift-wrapping'] || !!a['gift-note'];
    const shouldClearNewCard = hasNewCard && (hasMcGill(c) || c.item_count === 0);
    if (!shouldClearNewCard && !hasLegacyGift) return;

    saving = true;
    updateCheckoutState();
    try {
      cart = await updateAttributes({
        ...(shouldClearNewCard ? { 'Complimentary Message Card': '', 'Card Message': '' } : {}),
        ...(hasLegacyGift ? { 'gift-wrapping': '', 'gift-note': '' } : {})
      });
      if (shouldClearNewCard) draft = { on: false, text: '', leaveBlank: false };
    } catch (error) {
      // Keep storefront usable even if cleanup fails.
    } finally {
      saving = false;
      if (cart) render(cart);
    }
  }

  function render(c, options = {}) {
    cart = c;
    styles();
    cleanLegacyUi();

    if (!regularOnly(c)) {
      document.querySelectorAll('[data-mmp-card]').forEach((el) => el.remove());
      clearTimeout(cleanupTimer);
      cleanupTimer = setTimeout(() => clearIncompatibleAttributes(c), 0);
      return;
    }

    if (!options.preserveDraft && !dirty && !saving) draft = stateFromCart(c);

    targets().forEach(([key, parent, before]) => {
      let el = parent.querySelector(`[data-mmp-card="${key}"]`);
      if (!el) {
        el = createCard(key);
        parent.insertBefore(el, before);
      }
      syncCard(el);
    });

    updateCheckoutState();
    clearTimeout(cleanupTimer);
    cleanupTimer = setTimeout(() => clearIncompatibleAttributes(c), 0);
  }

  document.addEventListener('change', (event) => {
    const card = event.target.closest?.('[data-mmp-card]');
    if (!card || !cart || !regularOnly(cart)) return;

    if (event.target.matches('[data-mmp-card-toggle]')) {
      draft.on = event.target.checked;
      if (!draft.on) {
        draft.text = '';
        draft.leaveBlank = false;
      }
      syncAllCards();
      queueSave(0);
      return;
    }

    if (event.target.matches('[data-mmp-card-blank]')) {
      draft.leaveBlank = event.target.checked;
      if (draft.leaveBlank) draft.text = '';
      syncAllCards();
      queueSave(0);
      return;
    }

    if (event.target.matches('[data-mmp-card-message]') && draft.on) {
      draft.text = event.target.value;
      draft.leaveBlank = false;
      syncAllCards();
      queueSave(0);
    }
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('[data-mmp-card-message]')) return;
    const card = event.target.closest?.('[data-mmp-card]');
    if (!card || !cart || !regularOnly(cart) || !draft.on) return;

    draft.text = event.target.value;
    draft.leaveBlank = false;
    syncAllCards();
    queueSave(400);
  });

  document.addEventListener('submit', (event) => {
    const form = event.target.closest?.('form#cart');
    if (!form || !cart || !regularOnly(cart)) return;
    if (!checkoutBlocked()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (dirty && !saving) flushSave();
    syncAllCards();
  }, true);

  async function refresh() {
    if (saving || dirty) return;
    try {
      render(await readCart());
    } catch (error) {
      // No action needed.
    }
  }

  function later() {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, 220);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();

  const observer = new MutationObserver((mutations) => {
    const meaningful = mutations.some((mutation) => [...mutation.addedNodes].some((node) =>
      node.nodeType === 1 && !node.closest?.('[data-mmp-card]')
    ));
    if (meaningful) {
      cleanLegacyUi();
      later();
    }
  });

  const start = () => document.body && observer.observe(document.body, { childList: true, subtree: true });
  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
